/**
 * Treatment Record — Invoice drawer (PRD.182).
 *
 * Right drawer that hosts the existing InvoicesTab scoped to the resolved
 * encounter for the selected date. The header includes a `DateChipPopover`
 * so the user can change the target date inline; switching dates re-resolves
 * the underlying Visit Note via `ensureEncounterForDate`.
 *
 * Drawer width follows the configured Treatment Record drawer width preset.
 */
import { useEffect, useState, type CSSProperties } from 'react';
import { Drawer } from '@helios/ui/overlay';
import { useAuth } from '@helios/auth-firebase-web';
import { useBrandTokens } from '../../../shell/shared';
import { usePowerSyncDb, usePowerSyncQuery } from '../../../adapters/powersync';
import { ensureEncounterForDate } from '../../../services/encounter-service';
import { InvoicesTab } from '../consultation/InvoicesTab';
import { DateChipPopover } from './DateChipPopover';
import {
  qEncounterById,
  type PatientEncounterRow,
  type TreatmentRecordDrawerWidth,
} from '@helios/data-access';
import { DRAWER_WIDTH_BY_PRESET } from './TreatmentChartingFilesDrawer';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  patientId: string;
  initialEncounterId: string | null;
  initialDate: string | null;
  drawerWidth: TreatmentRecordDrawerWidth;
  /**
   * Drawer header title. Defaults to "Billing" because the surface
   * includes invoice details + inline Payments + Adjustments sections,
   * so "Add Invoice" no longer captures everything inside.
   */
  title?: string;
}

export function TreatmentInvoiceDrawer({
  open,
  onOpenChange,
  patientId,
  initialEncounterId,
  initialDate,
  drawerWidth,
  title = 'Billing',
}: Props) {
  const brandTokens = useBrandTokens();
  const { db } = usePowerSyncDb();
  const { claims } = useAuth();
  const orgId = claims?.activeOrg ?? '';

  const [encounterId, setEncounterId] = useState<string | null>(initialEncounterId);
  const [date, setDate] = useState<string | null>(initialDate);
  const [resolving, setResolving] = useState(false);

  useEffect(() => {
    if (open) {
      setEncounterId(initialEncounterId);
      setDate(initialDate);
    }
  }, [open, initialEncounterId, initialDate]);

  const handleDateChange = async (newDate: string) => {
    if (!db || !orgId || !patientId) return;
    if (newDate === date) return;
    setDate(newDate);
    setResolving(true);
    try {
      const { encounterId: newEncounterId } = await ensureEncounterForDate({
        db,
        orgId,
        patientId,
        date: newDate,
      });
      setEncounterId(newEncounterId);
    } catch (err) {
      console.error('[TreatmentInvoiceDrawer] resolve encounter:', err);
    } finally {
      setResolving(false);
    }
  };

  const { data: encounterRows = [] } = usePowerSyncQuery<PatientEncounterRow>(
    qEncounterById(encounterId ?? ''),
  );
  const visitId = encounterRows[0]?.visit_id ?? undefined;
  const ready = !!encounterId && !resolving;

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      portalStyle={brandTokens as CSSProperties}
      width={DRAWER_WIDTH_BY_PRESET[drawerWidth]}
    >
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
        {date && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              padding: '8px var(--sp-5)',
              borderBottom: '1px solid var(--border)',
              background: 'var(--bg-surface)',
            }}
          >
            <DateChipPopover patientId={patientId} date={date} onChange={handleDateChange} />
          </div>
        )}
        {!ready ? (
          <div
            style={{
              padding: 'var(--sp-6)',
              color: 'var(--text-muted)',
              fontSize: 'var(--font-size-sm)',
            }}
          >
            {resolving ? 'Switching date…' : 'Loading…'}
          </div>
        ) : (
          <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: 'var(--sp-5)' }}>
            <InvoicesTab
              key={encounterId ?? ''}
              patientId={patientId}
              encounterId={encounterId!}
              visitId={visitId ?? undefined}
              // PRD.182: in Treatment Record Workspace mode, every entered
              // invoice item should land in the treatment record by default.
              defaultAddToTreatment
            />
          </div>
        )}
      </div>
    </Drawer>
  );
}
